import React, { useState, useEffect, useCallback } from 'react';
import { statsApi } from '../../services/api';
import './Revenue.css';

/**
 * Issue #313: Ad Revenue KPI Dashboard
 * Displays revenue metrics from Google Ads/AdMob
 */
function Revenue() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    summary: {
      total_revenue: 0,
      revenue_today: 0,
      revenue_7_days: 0,
      ecpm: 0,
      arpdau: 0,
      fill_rate: 0,
      ctr: 0,
      impressions_per_user: 0,
      revenue_per_session: 0
    },
    daily: [],
    monthly: [],
    universities: [],
    ad_formats: []
  });

  // Date range filter
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 12);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  // Multi-select filters
  const [selectedUniversities, setSelectedUniversities] = useState([]);
  const [selectedFormats, setSelectedFormats] = useState([]);

  // Graph type toggle
  const [dailyGraphType, setDailyGraphType] = useState('line');
  const [monthlyGraphType, setMonthlyGraphType] = useState('line');

  // Export selection
  const [exportKpis, setExportKpis] = useState({
    revenue: true,
    impressions: true,
    ecpm: true,
    arpdau: true,
    fill_rate: true,
    ctr: true
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await statsApi.getRevenue(
        startDate,
        endDate,
        selectedUniversities.length > 0 ? selectedUniversities : null,
        selectedFormats.length > 0 ? selectedFormats : null
      );
      setData(response.data);
    } catch (err) {
      console.error('Failed to fetch revenue data:', err);
      setError('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedUniversities, selectedFormats]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    const selectedKpisList = Object.entries(exportKpis)
      .filter(([_, selected]) => selected)
      .map(([kpi]) => kpi);

    if (selectedKpisList.length === 0) {
      alert('Bitte mindestens einen KPI zum Exportieren auswaehlen.');
      return;
    }

    try {
      const response = await statsApi.exportRevenue(
        startDate,
        endDate,
        selectedUniversities.length > 0 ? selectedUniversities : null,
        selectedFormats.length > 0 ? selectedFormats : null,
        selectedKpisList
      );
      // Create download link
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ad-revenue-${startDate}-${endDate}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export fehlgeschlagen');
    }
  };

  const handleUniversityToggle = (uni) => {
    setSelectedUniversities(prev =>
      prev.includes(uni)
        ? prev.filter(u => u !== uni)
        : [...prev, uni]
    );
  };

  const handleFormatToggle = (format) => {
    setSelectedFormats(prev =>
      prev.includes(format)
        ? prev.filter(f => f !== format)
        : [...prev, format]
    );
  };

  // Calculate max values for scaling graphs
  const maxDailyRevenue = Math.max(...data.daily.map(d => d.revenue), 1);
  const maxMonthlyRevenue = Math.max(...data.monthly.map(d => d.revenue), 1);

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  // Format percentage
  const formatPercent = (value) => {
    return `${value.toFixed(2)}%`;
  };

  if (loading) {
    return (
      <div className="revenue-container">
        <h1>Umsatz</h1>
        <div className="loading">Lade Daten...</div>
      </div>
    );
  }

  return (
    <div className="revenue-container">
      <h1>Umsatz</h1>

      {error && <div className="error-message">{error}</div>}

      {/* Filter Section */}
      <div className="filter-section">
        <div className="date-filters">
          <label>
            Von:
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </label>
          <label>
            Bis:
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </label>
          <button onClick={fetchData} className="refresh-btn">
            Aktualisieren
          </button>
        </div>

        {/* University Filter */}
        {data.universities.length > 0 && (
          <div className="multi-filter">
            <span className="filter-label">Universitaet:</span>
            <div className="filter-chips">
              {data.universities.map(uni => (
                <button
                  key={uni}
                  className={`filter-chip ${selectedUniversities.includes(uni) ? 'active' : ''}`}
                  onClick={() => handleUniversityToggle(uni)}
                >
                  {uni}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Ad Format Filter */}
        {data.ad_formats.length > 0 && (
          <div className="multi-filter">
            <span className="filter-label">Anzeigenformat:</span>
            <div className="filter-chips">
              {data.ad_formats.map(format => (
                <button
                  key={format}
                  className={`filter-chip ${selectedFormats.includes(format) ? 'active' : ''}`}
                  onClick={() => handleFormatToggle(format)}
                >
                  {format}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Summary KPIs */}
      <div className="kpi-summary">
        <div className="kpi-card primary">
          <span className="kpi-value">{formatCurrency(data.summary.total_revenue)}</span>
          <span className="kpi-label">Gesamtumsatz</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-value">{formatCurrency(data.summary.revenue_today)}</span>
          <span className="kpi-label">Umsatz heute</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-value">{formatCurrency(data.summary.revenue_7_days)}</span>
          <span className="kpi-label">Letzte 7 Tage</span>
        </div>
      </div>

      {/* Detailed KPIs */}
      <div className="kpi-grid">
        <div className="kpi-item">
          <span className="kpi-title">eCPM</span>
          <span className="kpi-value-small">{formatCurrency(data.summary.ecpm)}</span>
          <span className="kpi-description">(Umsatz/Impressionen)*1000</span>
        </div>
        <div className="kpi-item">
          <span className="kpi-title">ARPDAU</span>
          <span className="kpi-value-small">{formatCurrency(data.summary.arpdau)}</span>
          <span className="kpi-description">Umsatz pro taglich aktivem Nutzer</span>
        </div>
        <div className="kpi-item">
          <span className="kpi-title">Fill Rate</span>
          <span className="kpi-value-small">{formatPercent(data.summary.fill_rate)}</span>
          <span className="kpi-description">Impressionen/Anfragen</span>
        </div>
        <div className="kpi-item">
          <span className="kpi-title">CTR</span>
          <span className="kpi-value-small">{formatPercent(data.summary.ctr * 100)}</span>
          <span className="kpi-description">Klicks/Impressionen</span>
        </div>
        <div className="kpi-item">
          <span className="kpi-title">Impressionen/Nutzer</span>
          <span className="kpi-value-small">{data.summary.impressions_per_user.toFixed(1)}</span>
          <span className="kpi-description">Impressionen pro DAU</span>
        </div>
        <div className="kpi-item">
          <span className="kpi-title">Umsatz/Session</span>
          <span className="kpi-value-small">{formatCurrency(data.summary.revenue_per_session)}</span>
          <span className="kpi-description">Umsatz pro Sitzung</span>
        </div>
      </div>

      {/* Export Section */}
      <div className="export-section">
        <h3>Daten exportieren</h3>
        <div className="export-options">
          <label>
            <input
              type="checkbox"
              checked={exportKpis.revenue}
              onChange={(e) => setExportKpis({...exportKpis, revenue: e.target.checked})}
            />
            Umsatz
          </label>
          <label>
            <input
              type="checkbox"
              checked={exportKpis.impressions}
              onChange={(e) => setExportKpis({...exportKpis, impressions: e.target.checked})}
            />
            Impressionen
          </label>
          <label>
            <input
              type="checkbox"
              checked={exportKpis.ecpm}
              onChange={(e) => setExportKpis({...exportKpis, ecpm: e.target.checked})}
            />
            eCPM
          </label>
          <label>
            <input
              type="checkbox"
              checked={exportKpis.arpdau}
              onChange={(e) => setExportKpis({...exportKpis, arpdau: e.target.checked})}
            />
            ARPDAU
          </label>
          <label>
            <input
              type="checkbox"
              checked={exportKpis.fill_rate}
              onChange={(e) => setExportKpis({...exportKpis, fill_rate: e.target.checked})}
            />
            Fill Rate
          </label>
          <label>
            <input
              type="checkbox"
              checked={exportKpis.ctr}
              onChange={(e) => setExportKpis({...exportKpis, ctr: e.target.checked})}
            />
            CTR
          </label>
          <button onClick={handleExport} className="export-btn">
            Als Excel exportieren
          </button>
        </div>
      </div>

      {/* Daily Revenue Graph */}
      <div className="kpi-section">
        <div className="kpi-header">
          <h2>Taglicher Umsatz</h2>
          <div className="graph-toggle">
            <button
              className={dailyGraphType === 'line' ? 'active' : ''}
              onClick={() => setDailyGraphType('line')}
            >
              Linie
            </button>
            <button
              className={dailyGraphType === 'bar' ? 'active' : ''}
              onClick={() => setDailyGraphType('bar')}
            >
              Balken
            </button>
          </div>
        </div>
        <p className="kpi-description">Umsatzentwicklung der letzten 30 Tage</p>
        <div className="graph-container">
          {data.daily.length === 0 ? (
            <div className="no-data">
              Keine Daten verfuegbar. Daten werden angezeigt, sobald die Google API Integration abgeschlossen ist.
            </div>
          ) : (
            <div className={`graph ${dailyGraphType}`}>
              {data.daily.map((point, idx) => (
                <div key={idx} className="graph-bar-container small">
                  <div
                    className="graph-bar revenue"
                    style={{ height: `${(point.revenue / maxDailyRevenue) * 100}%` }}
                    title={`${point.date}: ${formatCurrency(point.revenue)}`}
                  >
                    {dailyGraphType === 'bar' && (
                      <span className="bar-value-small">{point.revenue.toFixed(0)}</span>
                    )}
                  </div>
                  <span className="bar-label-small">{point.date.slice(5)}</span>
                </div>
              ))}
              {dailyGraphType === 'line' && data.daily.length > 0 && (
                <svg className="line-overlay" viewBox={`0 0 ${data.daily.length * 25} 200`} preserveAspectRatio="none">
                  <polyline
                    fill="none"
                    stroke="#28a745"
                    strokeWidth="2"
                    points={data.daily.map((point, idx) =>
                      `${idx * 25 + 12},${200 - (point.revenue / maxDailyRevenue) * 180}`
                    ).join(' ')}
                  />
                </svg>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Monthly Revenue Graph */}
      <div className="kpi-section">
        <div className="kpi-header">
          <h2>Monatlicher Umsatz</h2>
          <div className="graph-toggle">
            <button
              className={monthlyGraphType === 'line' ? 'active' : ''}
              onClick={() => setMonthlyGraphType('line')}
            >
              Linie
            </button>
            <button
              className={monthlyGraphType === 'bar' ? 'active' : ''}
              onClick={() => setMonthlyGraphType('bar')}
            >
              Balken
            </button>
          </div>
        </div>
        <p className="kpi-description">Umsatzentwicklung der letzten 12 Monate</p>
        <div className="graph-container">
          {data.monthly.length === 0 ? (
            <div className="no-data">
              Keine Daten verfuegbar. Daten werden angezeigt, sobald die Google API Integration abgeschlossen ist.
            </div>
          ) : (
            <div className={`graph ${monthlyGraphType}`}>
              {data.monthly.map((point, idx) => (
                <div key={idx} className="graph-bar-container">
                  <div
                    className="graph-bar revenue"
                    style={{ height: `${(point.revenue / maxMonthlyRevenue) * 100}%` }}
                    title={`${point.month}: ${formatCurrency(point.revenue)} (eCPM: ${formatCurrency(point.ecpm)})`}
                  >
                    <span className="bar-value">{formatCurrency(point.revenue)}</span>
                  </div>
                  <span className="bar-label">{point.month}</span>
                </div>
              ))}
              {monthlyGraphType === 'line' && (
                <svg className="line-overlay" viewBox={`0 0 ${data.monthly.length * 60} 200`} preserveAspectRatio="none">
                  <polyline
                    fill="none"
                    stroke="#28a745"
                    strokeWidth="2"
                    points={data.monthly.map((point, idx) =>
                      `${idx * 60 + 30},${200 - (point.revenue / maxMonthlyRevenue) * 180}`
                    ).join(' ')}
                  />
                </svg>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Info about data source */}
      <div className="info-section">
        <h3>Hinweis zur Datenquelle</h3>
        <p>
          Die Umsatzdaten werden von Google Ads (Web) und Google AdMob (Mobile) abgerufen.
          Die Aktualisierungsfrequenz ist wie folgt:
        </p>
        <ul>
          <li><strong>Gesamtumsatz & Umsatz heute:</strong> Alle 10 Minuten</li>
          <li><strong>Tagliche Metriken:</strong> Taeglich aktualisiert</li>
          <li><strong>Monatliche Uebersicht:</strong> Monatlich aktualisiert</li>
        </ul>
      </div>
    </div>
  );
}

export default Revenue;
